import { nodeTotals, isBreakdown } from "./treeMath"
import { quantityOf } from "./formatting"

export const serializeTree = (sections, deletedItems = []) => {
  const result = []

  const tempIdFor       = node => (node.id ? undefined : node._uid)
  const parentIdFor     = parent => (parent && parent.id ? parent.id : null)
  const parentTempIdFor = parent => (parent && !parent.id ? parent._uid : null)

  const isSaved         = node => !node._destroy || node.id
  const savedServices   = nodes => (nodes || []).filter(c => c.type === "service" && isSaved(c))
  const savedBreakdowns = nodes => (nodes || []).filter(c => isBreakdown(c) && isSaved(c))

  const descriptionAttrs = (description, order) => ({
    id:             description.id || undefined,
    temp_id:        tempIdFor(description),
    is_descriptive: true,
    description:    description.description || "",
    order,
  })

  // Flat, like the qlines: a sub-ouvrage's lines name it by id or temp id.
  const breakdownAttrs = (lines, parent = null) => [
    ...lines.filter(s => !s._destroy).flatMap((line, idx) => line.type === "descriptive" ? [descriptionAttrs(line, idx)] : [
      {
        id:                  line.id || undefined,
        temp_id:             tempIdFor(line),
        parent_id:           parentIdFor(parent),
        parent_temp_id:      parentTempIdFor(parent),
        cost_category:       line.cost_category || "materials",
        description:         line.description || "",
        unit:                line.unit || "",
        quantity:            parseFloat(line.quantity) || 0,
        unit_price:          parseFloat(line.unit_price) || 0,
        purchase_price:      line.purchase_price ? parseFloat(line.purchase_price) : 0,
        ref_product_distrib: line.ref_product_distrib || "",
        product_id:          line.product_id || null,
        order:               idx,
      },
      ...breakdownAttrs(savedServices(line.children), line),
    ]),
    ...lines.filter(s => s._destroy).map(line => ({ id: line.id, _destroy: "1" })),
  ]

  // A lot built without the flag keeps the one it has stored; all under an option is one.
  const optionAttrs = (node, inOption) => {
    if (inOption) return { is_option: true }
    return node.option === undefined ? {} : { is_option: !!node.option }
  }

  const walk = (node, parent, siblingIndex, inOption = false) => {
    if (node._destroy) return
    const childInOption = inOption || !!node.option

    const allChildren  = node.children || []
    const liveChildren = allChildren.filter(c => !c._destroy)
    const services     = savedServices(allChildren)
    const groups       = liveChildren.filter(c => c.type === "group")

    if (services.some(service => !service._destroy)) {
      result.push({
        id:             node.id || undefined,
        temp_id:        tempIdFor(node),
        parent_id:      parentIdFor(parent),
        parent_temp_id: parentTempIdFor(parent),
        description:    node.description || "",
        long_description: node.long_description || "",
        ref_product_distrib: node.ref_product_distrib ?? null,
        order:          siblingIndex,
        quantity:       quantityOf(node.quantity),
        unit:           node.unit || "",
        discount:       parseFloat(node.discount) || 0,
        vat_rate_id:    node.vat_rate?.id || null,
        hide_in_pdf:    false,
        ...optionAttrs(node, inOption),
        qline_breakdowns_attributes: breakdownAttrs(savedBreakdowns(allChildren)),
      })
      // A work carries its components and descriptions as breakdowns; a sub-lot
      // found there stays a child qline and has to be walked apart.
      groups.forEach((child, i) => walk(child, node, services.length + i, childInOption))
      return
    }

    const totals = nodeTotals(node)

    result.push({
      id:                      node.id || undefined,
      temp_id:                 tempIdFor(node),
      parent_id:               parentIdFor(parent),
      parent_temp_id:          parentTempIdFor(parent),
      description:             node.description || "",
      long_description:        node.long_description || "",
      ref_product_distrib:     node.ref_product_distrib ?? null,
      order:                   siblingIndex,
      quantity:                null,
      unit:                    null,
      discount:                0,
      subtotal_not_discounted: totals.total,
      purchase_price:          totals.cost,
      ...optionAttrs(node, inOption),
      // A work whose lines all went is saved as an empty one, still destroying them.
      qline_breakdowns_attributes: breakdownAttrs(savedBreakdowns(allChildren)),
    })

    groups.forEach((child, i) => walk(child, node, i, childInOption))
  }

  sections.forEach((section, i) => walk(section, null, i))

  deletedItems.forEach(item => {
    if (item.id) result.push({ id: item.id, _destroy: "1" })
  })

  return result
}
